import api from '../services/api';
import DuprProfile from '../components/dupr/DuprProfile';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Save, User, Lock, Bell, Plus, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { updateProfile, changePassword } from '../services/authService';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  getOwnerBookingNotificationSettings,
  updateOwnerBookingNotificationSettings,
} from '../services/notificationService';

export default function Settings() {
  const { user, isAdminDelegatedSession } = useAuth();

  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [notifications, setNotifications] = useState({
    emailBookings: true,
    emailCancellations: true,
    emailPayments: true,
    emailReminders: true,
    emailReports: false,
    smsBookings: false,
    pushNotifications: true,
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [bookingEmailSettings, setBookingEmailSettings] = useState({
    newBookingEmailEnabled: true,
    recipients: [],
  });
  const [bookingEmailDraft, setBookingEmailDraft] = useState('');
  const [savingBookingEmails, setSavingBookingEmails] = useState(false);
  const [payoutRecipients, setPayoutRecipients] = useState([]);
  const [payoutEmailDraft, setPayoutEmailDraft] = useState('');
  const [savingPayoutEmails, setSavingPayoutEmails] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    let active = true;
    getNotificationPreferences()
      .then((preferences) => {
        if (active) setNotifications((current) => ({ ...current, ...preferences }));
      })
      .catch(() => {
        // Keep the safe defaults visible if the endpoint is temporarily unavailable.
      });
    getOwnerBookingNotificationSettings()
      .then((settings) => {
        if (active) {
          setBookingEmailSettings({
            newBookingEmailEnabled: settings.newBookingEmailEnabled ?? true,
            recipients: settings.recipients || [],
          });
          setPayoutRecipients(settings.payoutRecipients || []);
        }
      })
      .catch(() => {
        // The default owner email is used by the API until settings are saved.
      });
    return () => { active = false; };
  }, []);

  function handleProfileChange(e) {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
    setProfileErrors((prev) => ({ ...prev, [name]: '' }));
  }

  function validatePassword() {
    const errs = {};
    if (!passwordForm.currentPassword) errs.currentPassword = 'Current password is required';
    if (!passwordForm.newPassword) errs.newPassword = 'New password is required';
    else if (passwordForm.newPassword.length < 6) errs.newPassword = 'Must be at least 6 characters';
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errs.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile(profile);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (!validatePassword()) return;

    setSavingPassword(true);
    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  }

  function handleNotificationChange(key) {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleSaveNotifications() {
    setSavingNotifications(true);
    try {
      const saved = await updateNotificationPreferences(notifications);
      setNotifications((current) => ({ ...current, ...saved }));
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save notification preferences');
    } finally {
      setSavingNotifications(false);
    }
  }

  function addBookingEmailRecipient() {
    const email = bookingEmailDraft.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    if (bookingEmailSettings.recipients.some((recipient) => recipient.toLowerCase() === email)) {
      toast.error('This email address is already included');
      return;
    }
    if (bookingEmailSettings.recipients.length >= 20) {
      toast.error('You can add up to 20 recipients');
      return;
    }

    setBookingEmailSettings((current) => ({
      ...current,
      recipients: [...current.recipients, email],
    }));
    setBookingEmailDraft('');
  }

  async function handleSaveBookingEmails() {
    setSavingBookingEmails(true);
    try {
      const saved = await updateOwnerBookingNotificationSettings({
        ...bookingEmailSettings,
        payoutRecipients,
      });
      setBookingEmailSettings({
        newBookingEmailEnabled: saved.newBookingEmailEnabled,
        recipients: saved.recipients || [],
      });
      setPayoutRecipients(saved.payoutRecipients || []);
      toast.success('Booking email alerts saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save booking email alerts');
    } finally {
      setSavingBookingEmails(false);
    }
  }

  function addPayoutEmailRecipient() {
    const email = payoutEmailDraft.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    if (payoutRecipients.some((recipient) => recipient.toLowerCase() === email)) {
      toast.error('This email address is already included');
      return;
    }
    if (payoutRecipients.length >= 20) {
      toast.error('You can add up to 20 recipients');
      return;
    }
    setPayoutRecipients((current) => [...current, email]);
    setPayoutEmailDraft('');
  }

  async function handleSavePayoutEmails() {
    setSavingPayoutEmails(true);
    try {
      const saved = await updateOwnerBookingNotificationSettings({
        ...bookingEmailSettings,
        payoutRecipients,
      });
      setBookingEmailSettings({
        newBookingEmailEnabled: saved.newBookingEmailEnabled,
        recipients: saved.recipients || [],
      });
      setPayoutRecipients(saved.payoutRecipients || []);
      toast.success('Payout email recipients saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save payout email recipients');
    } finally {
      setSavingPayoutEmails(false);
    }
  }

  return (
    <div className="space-y-6">
      <DuprProfile api={api} />
      <div>
        <h1 className="text-2xl font-bold text-[#1F2937]">Settings</h1>
        {user?.role === 'VenueManager' && <p className="mt-2 text-sm text-[#285B63]">Booking and payout email alerts are shared with the venue owner. Profile, password and personal notification preferences apply to your own account.</p>}
        <p className="mt-1 text-sm text-[#64748B]">Manage your account settings</p>
      </div>

      <div className="card p-6">
        <div className="flex items-center gap-2 mb-2">
          <Bell className="h-5 w-5 text-[#64748B]" />
          <h2 className="text-lg font-semibold text-[#1F2937]">Venue Booking Email Alerts</h2>
        </div>
        <p className="mb-6 text-sm text-[#64748B]">Send a booking alert after a player has successfully confirmed a booking on any of your courts.</p>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-1">
            <div>
              <p className="text-sm font-medium text-[#1F2937]">New booking emails</p>
              <p className="text-xs text-[#64748B]">Turn these email alerts on or off for all of your venues</p>
            </div>
            <button
              type="button"
              onClick={() => setBookingEmailSettings((current) => ({
                ...current,
                newBookingEmailEnabled: !current.newBookingEmailEnabled,
              }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                bookingEmailSettings.newBookingEmailEnabled ? 'bg-primary-600' : 'bg-[#2B2B2B]'
              }`}
              aria-pressed={bookingEmailSettings.newBookingEmailEnabled}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                bookingEmailSettings.newBookingEmailEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          <div className="border-t border-[#E2E8F0] pt-4">
            <label className="block text-sm font-medium text-[#1F2937]">Recipients</label>
            <p className="mt-1 text-xs text-[#64748B]">Your account email is included by default. Add staff or operations inboxes as needed (up to 20).</p>
            <div className="mt-3 flex gap-2">
              <input
                type="email"
                value={bookingEmailDraft}
                onChange={(e) => setBookingEmailDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addBookingEmailRecipient();
                  }
                }}
                disabled={!bookingEmailSettings.newBookingEmailEnabled}
                className="input-field flex-1"
                placeholder="staff@yourvenue.com"
              />
              <button
                type="button"
                onClick={addBookingEmailRecipient}
                disabled={!bookingEmailSettings.newBookingEmailEnabled}
                className="btn-secondary shrink-0 disabled:opacity-60"
              >
                <Plus className="mr-1 inline h-4 w-4" /> Add
              </button>
            </div>
            {bookingEmailSettings.recipients.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {bookingEmailSettings.recipients.map((email) => (
                  <span key={email} className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                    {email}
                    <button
                      type="button"
                      onClick={() => setBookingEmailSettings((current) => ({
                        ...current,
                        recipients: current.recipients.filter((recipient) => recipient !== email),
                      }))}
                      aria-label={`Remove ${email}`}
                      className="rounded-full p-0.5 hover:bg-primary-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-xs text-[#94A3B8]">No recipients configured.</p>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveBookingEmails}
            disabled={savingBookingEmails}
            className="btn-primary disabled:opacity-60"
          >
            <Save className="h-4 w-4 mr-2" />
            {savingBookingEmails ? 'Saving...' : 'Save Booking Alerts'}
          </button>
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center gap-2 mb-2">
          <Bell className="h-5 w-5 text-[#64748B]" />
          <h2 className="text-lg font-semibold text-[#1F2937]">Payout Email Alerts</h2>
        </div>
        <p className="mb-5 text-sm text-[#64748B]">These recipients are emailed when FREAC records a confirmed payout transfer.</p>
        <label className="block text-sm font-medium text-[#1F2937]">Payout recipients</label>
        <p className="mt-1 text-xs text-[#64748B]">Your owner account email is included initially. Add finance or accounting inboxes as needed (up to 20).</p>
        <div className="mt-3 flex gap-2">
          <input
            type="email"
            value={payoutEmailDraft}
            onChange={(e) => setPayoutEmailDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addPayoutEmailRecipient();
              }
            }}
            className="input-field flex-1"
            placeholder="finance@yourvenue.com"
          />
          <button type="button" onClick={addPayoutEmailRecipient} className="btn-secondary shrink-0">
            <Plus className="mr-1 inline h-4 w-4" /> Add
          </button>
        </div>
        {payoutRecipients.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {payoutRecipients.map((email) => (
              <span key={email} className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                {email}
                <button
                  type="button"
                  onClick={() => setPayoutRecipients((current) => current.filter((recipient) => recipient !== email))}
                  aria-label={`Remove ${email}`}
                  className="rounded-full p-0.5 hover:bg-primary-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-xs text-[#94A3B8]">No payout recipients configured.</p>
        )}
        <div className="mt-6 flex justify-end">
          <button onClick={handleSavePayoutEmails} disabled={savingPayoutEmails} className="btn-primary disabled:opacity-60">
            <Save className="h-4 w-4 mr-2" />
            {savingPayoutEmails ? 'Saving...' : 'Save Payout Alerts'}
          </button>
        </div>
      </div>

      {!isAdminDelegatedSession && <div className="card p-6">
        <div className="flex items-center gap-2 mb-6">
          <User className="h-5 w-5 text-[#64748B]" />
          <h2 className="text-lg font-semibold text-[#1F2937]">Profile Information</h2>
        </div>
        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[#64748B]">Full Name</label>
              <input
                type="text"
                name="name"
                value={profile.name}
                onChange={handleProfileChange}
                className="input-field mt-1"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#64748B]">Email</label>
              <input
                type="email"
                name="email"
                value={profile.email}
                onChange={handleProfileChange}
                className="input-field mt-1"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#64748B]">Phone</label>
              <input
                type="tel"
                name="phone"
                value={profile.phone}
                onChange={handleProfileChange}
                className="input-field mt-1"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={savingProfile} className="btn-primary">
              <Save className="h-4 w-4 mr-2" />
              {savingProfile ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>}

      {!isAdminDelegatedSession && <div className="card p-6">
        <div className="flex items-center gap-2 mb-6">
          <Lock className="h-5 w-5 text-[#64748B]" />
          <h2 className="text-lg font-semibold text-[#1F2937]">Change Password</h2>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#64748B]">Current Password</label>
            <input
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => {
                setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }));
                setPasswordErrors((prev) => ({ ...prev, currentPassword: '' }));
              }}
              className={`input-field mt-1 ${passwordErrors.currentPassword ? 'border-red-300' : ''}`}
              placeholder="Current password"
            />
            {passwordErrors.currentPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword}</p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-[#64748B]">New Password</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => {
                  setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }));
                  setPasswordErrors((prev) => ({ ...prev, newPassword: '' }));
                }}
                className={`input-field mt-1 ${passwordErrors.newPassword ? 'border-red-300' : ''}`}
                placeholder="New password"
              />
              {passwordErrors.newPassword && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#64748B]">Confirm New Password</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => {
                  setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }));
                  setPasswordErrors((prev) => ({ ...prev, confirmPassword: '' }));
                }}
                className={`input-field mt-1 ${passwordErrors.confirmPassword ? 'border-red-300' : ''}`}
                placeholder="Confirm new password"
              />
              {passwordErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={savingPassword} className="btn-primary">
              <Lock className="h-4 w-4 mr-2" />
              {savingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>}

      <div className="card p-6">
        <div className="flex items-center gap-2 mb-6">
          <Bell className="h-5 w-5 text-[#64748B]" />
          <h2 className="text-lg font-semibold text-[#1F2937]">Notification Preferences</h2>
        </div>
        <div className="space-y-4">
          {[
            { key: 'emailCancellations', label: 'Cancellation Emails', desc: 'Receive email when a booking is cancelled' },
            { key: 'emailPayments', label: 'Payment Emails', desc: 'Receive a receipt when a player payment is successful or fails' },
            { key: 'emailReminders', label: 'Booking Reminders', desc: 'Receive an email before an upcoming booking' },
            { key: 'emailReports', label: 'Weekly Reports', desc: 'Receive weekly summary reports via email' },
            { key: 'smsBookings', label: 'SMS Notifications', desc: 'Receive text messages for new bookings' },
            { key: 'pushNotifications', label: 'Push Notifications', desc: 'Receive alerts on your signed-in devices' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-[#1F2937]">{item.label}</p>
                <p className="text-xs text-[#64748B]">{item.desc}</p>
              </div>
              <button
                type="button"
                onClick={() => handleNotificationChange(item.key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notifications[item.key] ? 'bg-primary-600' : 'bg-[#2B2B2B]'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notifications[item.key] ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSaveNotifications}
            disabled={savingNotifications}
            className="btn-primary disabled:opacity-60"
          >
            <Save className="h-4 w-4 mr-2" />
            {savingNotifications ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>
    </div>
  );
}
